import { Box, Button, Divider, Paper, Tooltip, useMediaQuery } from "@mui/material"
import { useCallback, useEffect } from "react";
import { Prompt, useHistory, useLocation } from "react-router-dom";
import { Location } from 'history'

import { PageTitle } from "src/components/projetos/PageTitle"
import { BottomBar } from "src/framework/components/commons/BottomBar"
import { formState$ } from "src/store/vendas/projetos/store"
import { DEFAULT_PROJETO } from "../constants";
import { Projeto } from "../types";
import { ServicoInformationModal } from "../containers/ServicoInformationModal";
import DefaultLayout from "../edicao/layouts/DefaultLayout";
import { Equalizer } from "@mui/icons-material";
import { ServicoCodeModal } from "../containers/ServicoCodeModal";
import { PermissionUtil } from "src/framework/commons/PermissionUtil";
import theme from "src/theme/light";
import { ID_MEDIDOR, ID_MONITORAMENTO, ID_TRANSFORMADOR_CORRENTE } from "src/components/componentes/types";
import { MAIN_APP_CONTAINER_ID } from "src/framework/layouts/Main";

export interface LayoutProps {
  title: string;
  subtitle?: React.ReactNode;
  onClickBack: () => void;
  onClickNext: () => void;
  nextButtonDisabledTooltip?: string;
  nextButtonIsDisabled?: boolean;
  nextButtonLabel?: string;
  formState: Projeto
  showRoiPaybackButton?: boolean
  onClickRoiPaybackButton?: () => void
}

const Layout: React.FC<LayoutProps> = ({ onClickRoiPaybackButton = () => {}, showRoiPaybackButton = false, formState, children, title, nextButtonLabel = 'Continuar', nextButtonDisabledTooltip = '', subtitle, onClickBack, onClickNext, nextButtonIsDisabled = false }) => {
  const history = useHistory();
  const location = useLocation<{ allowAccess: boolean }>()
  const permissoes = PermissionUtil.getPermissions();
  const temGridZero =  formState?.acessorios?.some((c: any) => [ID_MEDIDOR, ID_TRANSFORMADOR_CORRENTE, ID_MONITORAMENTO].includes(c.categoria_componentes_id) && c.quantidade > 0)

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const FooterButton = isMobile ? Box : BottomBar;

  const redirectToStart = useCallback(()=>{
    formState$.next(DEFAULT_PROJETO)
    history.push("/projetos", { done: true })
  },[history])

  useEffect(()=>{
    // Limpa estado da rota
    window.history.replaceState({}, document.title)
  },[location, location.state])

  useEffect(()=>{
    if (history.location.pathname.includes('edicao') && !formState?.codigo_venda) return;
    if (formState?.isEdit) return;

    // Evita usuário acessar fluxo de criação sem passar pelo formulário inicial..
    const { allowAccess = false } = location.state || {}
    if (!allowAccess) {
      redirectToStart()
    }
  },[formState, history, location, redirectToStart])

  useEffect(()=>{
    if (!formState) return;
    if (!formState.tipo) return;
    if (formState.isEdit) {
      const routeTipo = history.location.pathname.split('/')?.[3]
      if (!routeTipo) return;
      if (!routeTipo.includes(formState.tipo)) {
        redirectToStart()
      }
    }
  },[formState, history, history.location.pathname, redirectToStart])
  
  useEffect(()=>{
    const el = document.getElementById(MAIN_APP_CONTAINER_ID)
    if (el) el.scrollIntoView()
  },[title])

  const canNavigate = useCallback((promptLocation: Location | null = null)=>{
    const allowedLocations = ['/novo$', '/edicao$', 'clonar$']
    const loc = (promptLocation || history.location) as Location<{ done: boolean; noReset: boolean }>
    return loc.state?.noReset || loc.state?.done || allowedLocations.some(item => {
      return loc.pathname.match(new RegExp(item))
    })
  },[history.location])

  useEffect(()=>{
    return () => {
      if (!canNavigate()) {
        formState$.next(DEFAULT_PROJETO)
      }
    }
  },[canNavigate])

  return (
    <DefaultLayout>
      <Paper>
        <Box p={2}>
          <PageTitle
            title={title}
            subtitle={subtitle || ''}
          />
        </Box>

        <Divider />
        {children}
      </Paper>
      <FooterButton style={isMobile ? { marginTop: 10, padding: 15 } : {}}>
        {(showRoiPaybackButton && PermissionUtil.can(PermissionUtil.PERMISSION_PROJETOS_EXIBIR_DADOS_GERACAO_PROPOSTA, permissoes)) && !temGridZero && (
          <Tooltip title="Gerar gráficos.">
            <Box onClick={() => onClickRoiPaybackButton()} border="none" component="button" display="flex" justifyContent={"center"} alignItems={"center"} width={isMobile ? "100%" : "40px"} height={"40px"} borderRadius="undefinedpx" style={{ cursor: 'pointer', background: 'linear-gradient(180deg, #1FB06A 0%, #1792B1 100%)'}} >
              <Equalizer htmlColor="white" />
            </Box>
          </Tooltip>
        )}
        <Button
          variant="outlined"
          color={"secondary"}
          onClick={onClickBack}
          fullWidth={isMobile}
          style={isMobile ? { marginTop: 15 } : {}}
        >
          Voltar
        </Button>
        <Tooltip title={nextButtonIsDisabled ? nextButtonDisabledTooltip : ''}>
          <span>
            <Button
              disableElevation={true}
              disabled={nextButtonIsDisabled}
              color={"primary"}
              variant={"contained"}
              onClick={onClickNext}
              fullWidth={isMobile}
              style={isMobile ? { marginTop: 15, marginBottom: 15 } : {}}
              >
              {nextButtonLabel}
            </Button>
          </span>
        </Tooltip>
      </FooterButton>
      <ServicoCodeModal />
      <ServicoInformationModal formState={formState} />
      <Prompt message={(location) => {
        if (canNavigate(location)) return true;
        return `Deseja cancelar a ${formState?.isEdit ? 'edição' : 'criação'} do projeto?`
      }}/>
    </DefaultLayout>
  );
}


export { Layout }